import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hasSetupFeePaid } from '../services/subscriptionService';
import { useNavigate } from 'react-router-dom';
import { Lock, CreditCard } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';

interface SetupFeeGateProps {
  children: ReactNode;
  featureName?: string;
  showPrompt?: boolean;
}

export function SetupFeeGate({
  children,
  featureName = 'this feature',
  showPrompt = true,
}: SetupFeeGateProps) {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [hasPaid, setHasPaid] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ADMIN BYPASS: Admins automatically pass setup fee gate
    if (isAdmin) {
      setHasPaid(true);
      setLoading(false);
      return;
    }

    const checkSetupFee = async () => {
      if (!user) {
        setLoading(false);
        setHasPaid(false);
        return;
      }

      try {
        const paid = await hasSetupFeePaid(user.id);
        setHasPaid(paid);
      } catch (error) {
        console.error('Error checking setup fee:', error);
        setHasPaid(false);
      } finally {
        setLoading(false);
      }
    };

    checkSetupFee();
  }, [user, isAdmin]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (hasPaid === true) {
    return <>{children}</>;
  }

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
          <Lock className="text-amber-600" size={32} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Setup Fee Required
        </h2>
        <p className="text-gray-600 mb-4">
          To access {featureName}, you need to complete your one-time setup payment of $197.
        </p>
        <p className="text-sm text-gray-500 mb-6">
          This one-time fee unlocks all features including Career Reports, AI Coach, Job Match AI, and more.
        </p>
        <Button
          onClick={() => navigate('/subscription')}
          className="bg-amber-600 text-white hover:bg-amber-700"
        >
          <CreditCard className="w-5 h-5 mr-2" />
          Complete Setup Payment
        </Button>
      </div>
    </div>
  );
}
